/**
 * @fileoverview Application entry point for the React application.
 * @description This file serves as the main entry point for the React application. It initializes
 * Sentry error tracking, sets the browser tab title from the configured app name, locates the
 * root DOM node, and renders the top-level App component wrapped in a Sentry error boundary and
 * StrictMode to surface potential issues during development.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Hardik Patel
 */

import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import App from '@/app/App'
import { appConfig } from '@/config/app-config'
import { initSentry } from '@/config/sentry'
import { logger } from '@/shared/utils/logger'

import '@/features/auth'
import '@/features/notifications'
import '@/features/sales'
import '@/features/banking'
import '@/features/user-invitations'
import '@/features/capabilities'
import '@/features/audit'

import '@/styles/index.css'

initSentry()

// index.html ships the production name as a static default; replace it with the configured,
// per-environment app name so every deployment's tab is distinguishable.
document.title = appConfig.name

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={<p>Something went wrong. Please refresh the page.</p>}
      onError={(error, componentStack) => {
        logger.error('Unhandled render error', { error, componentStack })
      }}
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
